import { ArrowUpRightIcon, CheckIcon, CircleAlertIcon, XIcon } from "lucide-react";

import {
  useLicense,
  useLicenseUsage,
  useStartTrial,
} from "@/api/license";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/utils";

function tierBadge(tier?: string | null): "online" | "warning" | "default" {
  if (tier === "enterprise") return "online";
  if (tier === "professional") return "warning";
  return "default";
}

function statusBadge(status?: string | null): "online" | "warning" | "error" {
  if (status === "active") return "online";
  if (status === "trial") return "warning";
  return "error";
}

function UsageBar({
  label,
  current,
  max,
}: {
  label: string;
  current: number;
  max: number;
}) {
  const unlimited = max === -1;
  const pct = unlimited
    ? 100
    : max === 0
      ? 0
      : Math.min(100, Math.round((current / max) * 100));
  const tone =
    unlimited || pct < 70
      ? "bg-emerald-500"
      : pct < 90
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="font-mono">
          {current} / {unlimited ? "∞" : max}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted">
        <div
          className={cn("h-2 rounded-full transition-all", tone)}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
    </div>
  );
}

export function LicensePage() {
  const { data: license, isLoading, isError, error, refetch } = useLicense();
  const { data: usage } = useLicenseUsage();
  const startTrial = useStartTrial();

  if (isLoading) return <p className="py-10 text-center text-sm">Loading…</p>;
  // ERROR IS NOT "NO LICENCE". Falling through to the empty render would show a
  // page implying the tenant has no licence, which is a fact about the account,
  // not about a failed request.
  if (isError)
    return (
      <div className="py-10 text-center text-sm">
        <p className="text-destructive">Could not load the licence.</p>
        <p className="mt-1 text-muted-foreground">
          {error instanceof Error ? error.message : "The request failed."}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 underline underline-offset-2"
        >
          Retry
        </button>
      </div>
    );

  if (!license) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">License</h1>
        <Card>
          <CardContent className="space-y-4 py-10 text-center">
            <CircleAlertIcon className="mx-auto h-10 w-10 text-amber-500" />
            <p className="text-sm">No license active for this tenant.</p>
            <p className="text-xs text-muted-foreground">
              Start a 30-day Professional trial or contact sales to activate
              a paid plan.
            </p>
            <div className="flex justify-center gap-2">
              <Button
                disabled={startTrial.isPending}
                onClick={() => startTrial.mutate("professional")}
              >
                {startTrial.isPending ? "Starting…" : "Start 30-day trial"}
              </Button>
              <Button asChild variant="outline">
                <a href="mailto:sales@seple.in">
                  <ArrowUpRightIcon className="mr-1 h-4 w-4" />
                  Contact sales
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const featureEntries = Object.entries(license.features ?? {});

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">License</h1>
          <p className="text-sm text-muted-foreground">
            Plan + entitlement for this tenant.
          </p>
        </div>
        <Button asChild>
          <a href="mailto:sales@seple.in?subject=SView360%20upgrade%20enquiry">
            <ArrowUpRightIcon className="mr-1 h-4 w-4" />
            Upgrade
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-lg">Plan</CardTitle>
            <Badge variant={tierBadge(license.tier)}>
              {license.tier.toUpperCase()}
            </Badge>
            <Badge variant={statusBadge(license.status)}>
              {license.status}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">License key</span>
            <code className="font-mono text-xs">{license.license_key}</code>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Issued</span>
            <span>{formatDate(license.issued_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Expires</span>
            <span>
              {license.expires_at
                ? `${formatDate(license.expires_at)} (${license.days_until_expiry ?? "?"} days)`
                : "Never"}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <UsageBar
            label="Cameras"
            current={usage?.cameras?.current ?? 0}
            max={usage?.cameras?.max ?? license.max_cameras}
          />
          <UsageBar
            label="Sites"
            current={usage?.sites?.current ?? 0}
            max={usage?.sites?.max ?? license.max_sites}
          />
          <UsageBar
            label="Users"
            current={usage?.users?.current ?? 0}
            max={usage?.users?.max ?? license.max_users}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {featureEntries.map(([name, enabled]) => (
              <div
                key={name}
                className={cn(
                  "flex items-center gap-2 rounded border p-2 text-sm",
                  enabled
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-zinc-300/30 bg-muted/40 text-muted-foreground",
                )}
              >
                {enabled ? (
                  <CheckIcon className="h-4 w-4 text-emerald-600" />
                ) : (
                  <XIcon className="h-4 w-4" />
                )}
                <span className="font-mono text-xs">{name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
